import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Mail, Lock, BarChart3, PieChart, Activity } from 'lucide-react';
import { toast } from 'sonner';
import WorldGlobe from '../components/ui/WorldGlobe';
import usePublicSignup, { PRIVATE_PILOT_MESSAGE } from '../components/auth/usePublicSignup';

const LOGO_URL = '/ciaolead-logo-full.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const publicSignup = usePublicSignup();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--bg-page)] world-stars" data-testid="login-page">
      {/* Left Panel - Form */}
      <div className="relative z-10 w-full lg:w-[42%] flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8 world-card p-8 lg:p-10">
          {/* Logo */}
          <div className="flex justify-center lg:justify-start">
            <img
              src={LOGO_URL}
              alt="CiaoLead"
              className="max-w-[200px] w-full h-auto object-contain"
              data-testid="login-logo"
            />
          </div>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0">
              <CardTitle className="text-3xl font-bold text-white">
                Welcome back
              </CardTitle>
              <CardDescription className="text-slate-400 text-base">
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-200 font-medium">
                    Email address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11 world-input"
                      data-testid="login-email-input"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-200 font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-11 world-input"
                      data-testid="login-password-input"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 world-btn-primary font-medium btn-press"
                  disabled={isLoading}
                  data-testid="login-submit-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>

              {publicSignup === false ? (
                <p className="mt-6 text-center text-sm text-slate-400" data-testid="login-private-pilot">
                  {PRIVATE_PILOT_MESSAGE}
                </p>
              ) : (
                <p className="mt-6 text-center text-sm text-slate-400">
                  Don't have an account?{' '}
                  <Link
                    to="/signup"
                    className="text-blue-400 hover:text-blue-300 font-medium"
                    data-testid="login-signup-link"
                  >
                    Create account
                  </Link>
                </p>
              )}

              {/* CL-LEGAL-IMPRESSUM-001: permanent link to the public legal notice */}
              <p className="mt-4 text-center text-xs">
                <Link
                  to="/impressum"
                  className="text-slate-400 hover:text-slate-200 underline underline-offset-2"
                  data-testid="login-impressum-link"
                >
                  Impressum
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Panel - Globe + tagline */}
      <div className="hidden lg:flex w-[58%] auth-bg-image relative overflow-hidden">
        <div className="absolute inset-0 auth-bg-overlay" />

        {/* Floating UI cards */}
        <div className="world-panel absolute left-[12%] top-[16%] p-3 flex items-center gap-2 opacity-80" aria-hidden="true">
          <BarChart3 className="h-5 w-5 text-blue-400" />
          <div className="h-1.5 w-16 rounded bg-blue-500/40" />
        </div>
        <div className="world-panel absolute right-[10%] top-[24%] p-3 opacity-80" aria-hidden="true">
          <Activity className="h-5 w-5 text-violet-400" />
        </div>
        <div className="world-panel absolute left-[14%] bottom-[16%] p-3 flex items-center gap-2 opacity-80" aria-hidden="true">
          <PieChart className="h-5 w-5 text-purple-400" />
          <div className="space-y-1">
            <div className="h-1.5 w-14 rounded bg-violet-500/40" />
            <div className="h-1.5 w-10 rounded bg-blue-500/30" />
          </div>
        </div>

        {/* Globe */}
        <div className="absolute right-[6%] top-1/2 -translate-y-1/2" aria-hidden="true">
          <WorldGlobe size={460} className="!relative" />
        </div>

        <div className="relative z-10 flex flex-col items-start justify-center w-full pl-16 pr-12">
          <div className="max-w-md">
            <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
              Decision-support for sales teams
            </h2>
            <p className="text-slate-300 text-lg">
              Qualify B2C e-commerce leads smarter and faster with CiaoLead.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
